import { AloftPrice, Improvements } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { FUNCTIONAL_DEPRECIATION_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import { selectAppliedFunctionalUtilityAdjsDisplay } from '../adjustmentSelectors/selectFunctionalUtilityAdj.ts';
import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import {
  calculateDifferenceInDepreciation,
  getDepreciationDifferenceString,
  getDepreciationRowLabel,
} from './utils.ts';

const getFunctionalDepreciationRow = (
  index: number,
  improvements: Improvements,
  depreciationDifference?: AloftPrice | string,
  appliedAdjustment?: string,
) => ({
  label: getDepreciationRowLabel(index),
  functionalObsolescence:
    priceJSONToPrice(improvements?.functionalDepreciation)?.toString(true) ?? '-',
  depreciationDifference: getDepreciationDifferenceString(depreciationDifference),
  appliedAdjustment: appliedAdjustment ?? '-',
});

const selectFunctionalDepreciationCommentaryData = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectAppliedFunctionalUtilityAdjsDisplay,
    selectAdjustmentSettings,
  ],
  (compSequence, improvementsEntities, appliedFunctionalUtilityAdjs, adjustmentSettings) => ({
    helperText: getCommentaryTableHelperText(adjustmentSettings, 'functionalUtility'),
    data: ['subject', ...compSequence].map((id, index) => {
      const improvements = improvementsEntities[id];
      if (!improvements) throw new Error('error');

      const depreciationDifference =
        id === 'subject'
          ? '-'
          : calculateDifferenceInDepreciation(
              improvementsEntities.subject?.functionalDepreciation,
              improvementsEntities?.[id]?.functionalDepreciation,
            );

      return getFunctionalDepreciationRow(
        index,
        improvements,
        depreciationDifference,
        id === 'subject' ? '-' : appliedFunctionalUtilityAdjs?.[id]?.value,
      );
    }),
  }),
);

export const selectFunctionalDepreciationCommentaryTableDisplay = createSelector(
  [selectFunctionalDepreciationCommentaryData],
  ({
    data,
    helperText,
  }): {
    helperText?: string;
    data: [string[], ...string[][]];
  } => ({
    helperText,
    data: [
      FUNCTIONAL_DEPRECIATION_TABLE_HEADERS,
      ...data.map(
        ({ label, functionalObsolescence, depreciationDifference, appliedAdjustment }) => [
          label,
          functionalObsolescence,
          depreciationDifference,
          appliedAdjustment,
        ],
      ),
    ],
  }),
);
